/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Redirect } from "react-router-dom";
import { checkTraineeManagePermission } from "./traineeAuth.js";
import { parseThaiEndDate, parseThaiDateRange, formatThaiDateRangeText } from "./dormitoryFeeCalculator.js";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";
import "moment/locale/th";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import Button from "components/CustomButtons/Button.js";
import { Modal, ModalHeader, ModalBody, ModalFooter, Badge, Nav, NavItem, NavLink } from "reactstrap";
import Pagination from "react-responsive-pagination";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";

// Icons
import SchoolIcon from "@material-ui/icons/School";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import PeopleIcon from "@material-ui/icons/People";
import HotelIcon from "@material-ui/icons/Hotel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ScheduleIcon from "@material-ui/icons/Schedule";
import CancelIcon from "@material-ui/icons/Cancel";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import FilterListIcon from "@material-ui/icons/FilterList";
import DateRangeIcon from "@material-ui/icons/DateRange";
import TableChartIcon from "@material-ui/icons/TableChart";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";

moment.locale("th");

// ฟังก์ชันคำนวณปีงบประมาณไทย (1 ต.ค. - 30 ก.ย.)
export const calculateThaiFiscalYear = (dateInput = new Date()) => {
  if (!dateInput) return null;
  const d = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (isNaN(d.getTime())) return null;
  const month = d.getMonth() + 1; // 1 - 12
  const yearCE = d.getFullYear();
  // เดือน 10, 11, 12 นับเป็นปีงบประมาณถัดไป
  return month >= 10 ? yearCE + 543 + 1 : yearCE + 543;
};

// ฟังก์ชันคำนวณเดือนที่เข้าฝึก (ต.ค. - ก.ย.) จากวันที่จริง หรือ dateRangeText หรือ flags
export const getAttendingMonths = (item) => {
  if (!item) return [];
  const months = new Set();

  // 1. จาก DB flags ถ้ามีเป็น true
  if (item.month_Oct) months.add("ต.ค.");
  if (item.month_Nov) months.add("พ.ย.");
  if (item.month_Dec) months.add("ธ.ค.");
  if (item.month_Jan) months.add("ม.ค.");
  if (item.month_Feb) months.add("ก.พ.");
  if (item.month_Mar) months.add("มี.ค.");
  if (item.month_Apr) months.add("เม.ย.");
  if (item.month_May) months.add("พ.ค.");
  if (item.month_Jun) months.add("มิ.ย.");
  if (item.month_Jul) months.add("ก.ค.");
  if (item.month_Aug) months.add("ส.ค.");
  if (item.month_Sep) months.add("ก.ย.");

  // 2. จาก startDate และ endDate
  const rawStart = item.startDate;
  const rawEnd = item.endDate || item.startDate;
  if (rawStart) {
    const start = new Date(rawStart);
    const end = rawEnd ? new Date(rawEnd) : start;
    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
      const cur = new Date(start.getFullYear(), start.getMonth(), 1);
      const last = new Date(end.getFullYear(), end.getMonth(), 1);
      const THAI_MONTH_NAMES = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
      while (cur <= last) {
        months.add(THAI_MONTH_NAMES[cur.getMonth()]);
        cur.setMonth(cur.getMonth() + 1);
      }
    }
  }

  // 3. จาก dateRangeText ถ้ายังว่าง
  const txt = item.dateRangeText || item.dateRange || "";
  if (txt) {
    const THAI_FULL_MONTH_MAP = {
      "มกราคม": "ม.ค.", "กุมภาพันธ์": "ก.พ.", "มีนาคม": "มี.ค.", "เมษายน": "เม.ย.",
      "พฤษภาคม": "พ.ค.", "มิถุนายน": "มิ.ย.", "กรกฎาคม": "ก.ค.", "สิงหาคม": "ส.ค.",
      "กันยายน": "ก.ย.", "ตุลาคม": "ต.ค.", "พฤศจิกายน": "พ.ย.", "ธันวาคม": "ธ.ค."
    };
    for (const [full, short] of Object.entries(THAI_FULL_MONTH_MAP)) {
      if (txt.includes(full)) months.add(short);
    }
    const THAI_SHORT_MONTHS = ["ต.ค.", "พ.ย.", "ธ.ค.", "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย."];
    for (const short of THAI_SHORT_MONTHS) {
      if (txt.includes(short)) months.add(short);
    }
  }

  // 4. ถ้ามี item.months ที่มีค่าอยู่แล้ว
  if (Array.isArray(item.months)) {
    item.months.forEach(m => {
      if (typeof m === "string" && m.length <= 4) months.add(m);
    });
  }

  return Array.from(months);
};

const MONTH_COLS = [
  { key: "ต.ค.", label: "ตุลาคม" },
  { key: "พ.ย.", label: "พฤศจิกายน" },
  { key: "ธ.ค.", label: "ธันวาคม" },
  { key: "ม.ค.", label: "มกราคม" },
  { key: "ก.พ.", label: "กุมภาพันธ์" },
  { key: "มี.ค.", label: "มีนาคม" },
  { key: "เม.ย.", label: "เมษายน" },
  { key: "พ.ค.", label: "พฤษภาคม" },
  { key: "มิ.ย.", label: "มิถุนายน" },
  { key: "ก.ค.", label: "กรกฎาคม" },
  { key: "ส.ค.", label: "สิงหาคม" },
  { key: "ก.ย.", label: "กันยายน" }
];

const BUILDINGS_OPTIONS = [
  { id: 1, name: "ตึก 1", floors: [1, 2, 3] },
  { id: 2, name: "ตึก 2", floors: [1, 2, 3] },
  { id: 3, name: "ตึก 3", floors: [1, 2, 3] },
  { id: 4, name: "ตึก 4", floors: [1, 2, 3] }
];

const ROOM_OPTIONS = [
  // ตึก 1 (10 ห้อง): 111, 112, 121, 122, 123, 124, 131, 132, 133, 134
  "111", "112", "121", "122", "123", "124", "131", "132", "133", "134",
  // ตึก 2 (10 ห้อง): 211, 212, 221, 222, 223, 224, 231, 232, 233, 234
  "211", "212", "221", "222", "223", "224", "231", "232", "233", "234",
  // ตึก 3 (10 ห้อง): 311, 312, 321, 322, 323, 324, 331, 332, 333, 334
  "311", "312", "321", "322", "323", "324", "331", "332", "333", "334",
  // ตึก 4 (10 ห้อง): 411, 412, 421, 422, 423, 424, 431, 432, 433, 434
  "411", "412", "421", "422", "423", "424", "431", "432", "433", "434",
  "ไม่ได้พัก"
];

// รายชื่อแพทย์เริ่มต้น (ดึงจากฐานข้อมูลจริง 100%)
const DEFAULT_DOCTOR_LIST = [];

// รายชื่อนักศึกษาเริ่มต้น (ดึงจากฐานข้อมูลจริง 100%)
const DEFAULT_STUDENT_LIST = [];

const useStyles = makeStyles({
  container: {
    padding: "15px",
    fontFamily: '"Sarabun", sans-serif'
  },
  cardTitle: {
    fontFamily: '"Sarabun", sans-serif',
    fontWeight: "bold",
    fontSize: "1.25rem",
    marginBottom: "5px"
  },
  statValue: {
    fontSize: "1.8rem",
    fontWeight: "bold",
    lineHeight: "1.2"
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "0.875rem"
  },
  th: {
    backgroundColor: "#f4f6f9",
    color: "#333",
    fontWeight: "600",
    padding: "10px 8px",
    borderBottom: "2px solid #dee2e6",
    whiteSpace: "nowrap",
    textAlign: "left"
  },
  thCenter: {
    backgroundColor: "#f4f6f9",
    color: "#333",
    fontWeight: "600",
    padding: "10px 8px",
    borderBottom: "2px solid #dee2e6",
    whiteSpace: "nowrap",
    textAlign: "center"
  },
  td: {
    padding: "9px 8px",
    borderBottom: "1px solid #e9ecef",
    verticalAlign: "middle"
  },
  matrixCell: {
    textAlign: "center",
    padding: "6px 2px",
    borderBottom: "1px solid #e9ecef",
    borderLeft: "1px solid #f0f0f0",
    fontWeight: "bold",
    fontSize: "0.85rem"
  }
});

export default function TraineeList() {
  const classes = useStyles();

  // ป้องกันการเข้าถึงโดยตรง หากไม่มีสิทธิ์ ให้ redirect ไปหน้า Dashboard
  if (!checkTraineeManagePermission()) {
    return <Redirect to="/trainee/dashboard" />;
  }

  // Active Main Tab: 0 = แพทย์, 1 = นักศึกษาฝึกงาน, 2 = ปฏิทินรายเดือน
  const [activeTab, setActiveTab] = useState(0);

  // Data lists - กำหนดค่าเริ่มต้นด้วยรายการข้อมูลผู้ฝึกจริง (ทำให้หน้าเว็บแสดงข้อมูลทันที ไม่เป็น 0 แม้ API ออฟไลน์)
  const [doctorList, setDoctorList] = useState([]);
  const [studentList, setStudentList] = useState([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("ALL");
  const currentFiscalYearStr = useMemo(() => String(calculateThaiFiscalYear(new Date()) || "2570"), []);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(() => String(calculateThaiFiscalYear(new Date()) || "2570")); // ค่าเริ่มต้นเป็นปีงบประมาณปัจจุบัน
  const [trainingStatusFilter, setTrainingStatusFilter] = useState("ACTIVE"); // "ACTIVE" (กำลังฝึกปัจจุบัน), "UPCOMING" (รอเริ่มฝึก), "PAST" (ที่ผ่านมาแล้ว/ประวัติ), "ALL" (ทั้งหมด)
  const [selectedDormStatus, setSelectedDormStatus] = useState("ALL");
  const [matrixTypeFilter, setMatrixTypeFilter] = useState("ALL"); // "ALL", "DOCTOR", "STUDENT"
  const [matrixMonthFilter, setMatrixMonthFilter] = useState("ALL"); // "ALL", "ต.ค.", "พ.ย.", etc.

  // ปรับเปลี่ยนปีงบประมาณพร้อมปรับสถานะการฝึกอบรมให้สัมพันธ์กับปีที่เลือกโดยอัตโนมัติ
  const handleFiscalYearChange = (newYear) => {
    setSelectedFiscalYear(newYear);
    setCurrentPage(1);
    if (newYear === "ALL") {
      setTrainingStatusFilter("ALL");
    } else if (Number(newYear) < Number(currentFiscalYearStr)) {
      setTrainingStatusFilter("PAST");
    } else if (Number(newYear) > Number(currentFiscalYearStr)) {
      setTrainingStatusFilter("UPCOMING");
    } else {
      setTrainingStatusFilter("ACTIVE");
    }
  };

  // รายชื่อกลุ่มงานจากฐานข้อมูลโรงพยาบาล (dbo.Department)
  const [departmentsList, setDepartmentsList] = useState([
    "กลุ่มงานการแพทย์",
    "กลุ่มงานพยาบาลผู้ป่วยนอก",
    "กลุ่มงานเภสัชกรรม",
    "กลุ่มงานพยาธิวิทยาและความชันสูตร",
    "กลุ่มงานดิจิทัลการแพทย์",
    "กลุ่มงานวิจัยและถ่ายทอดเทคโนโลยี"
  ]);

  const [loadingData, setLoadingData] = useState(false);
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ดึงข้อมูลแพทย์และนักศึกษาจริงจาก Database API พร้อมสถานะห้องพัก
  const fetchTraineeData = async () => {
    if (!isMountedRef.current) return;
    setLoadingData(true);
    try {
      const [docRes, stuRes, stayRes] = await Promise.allSettled([
        axios.get("/api/v1/trainee/doctors"),
        axios.get("/api/v1/trainee/students"),
        axios.get("/api/v1/dormitory/stays")
      ]);
      if (!isMountedRef.current) return;

      const activeStays = (stayRes.status === "fulfilled" && stayRes.value?.data?.data && Array.isArray(stayRes.value.data.data))
        ? stayRes.value.data.data.filter(st => st.isActive !== false && (st.isCurrentStay ?? st.IsCurrentStay ?? true))
        : [];

      if (docRes.status === "fulfilled" && docRes.value?.data?.data && docRes.value.data.data.length > 0) {
        const rawDocs = docRes.value.data.data;
        const mappedDocs = rawDocs.map(d => {
          let sDate = (d.startDate || d.StartDate || "").toString().split("T")[0];
          let eDate = (d.endDate || d.EndDate || "").toString().split("T")[0];
          const rText = d.dateRangeText || d.DateRangeText || "";
          if ((!sDate || !eDate) && rText) {
            const parsed = parseThaiDateRange(rText, d.fiscalYear || d.FiscalYear || 2569);
            if (!sDate && parsed.start) sDate = parsed.start;
            if (!eDate && parsed.end) eDate = parsed.end;
          }
          const finalRange = rText || formatThaiDateRangeText(sDate, eDate);

          const docNameTH = d.fullNameTH || d.FullNameTH || "";
          const cleanDocName = docNameTH.replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
          const matchedStay = activeStays.find(st => {
            const stName = (st.traineeName || st.TraineeName || "").replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
            return cleanDocName && stName && (cleanDocName === stName || cleanDocName.includes(stName) || stName.includes(cleanDocName));
          });

          const hasDormStay = Boolean(matchedStay || d.assignedRoomNumber || d.AssignedRoomNumber || d.requiresAccommodation || d.RequiresAccommodation);
          const roomNumber = matchedStay?.roomNumber || matchedStay?.RoomNumber || d.assignedRoomNumber || d.AssignedRoomNumber || (hasDormStay ? "รอระบุห้อง" : "ไม่ได้พัก");
          const dormPaymentStatus = matchedStay?.paymentStatus || matchedStay?.PaymentStatus || "PAID";

          return {
            id: d.doctorID || d.DoctorID,
            type: "DOCTOR",
            bookNo: d.bookNo || d.BookNo || "",
            institution: d.institutionName || d.InstitutionName || "",
            nameTH: docNameTH,
            nameEN: d.fullNameEN || d.FullNameEN || "",
            position: d.position || d.Position || "นายแพทย์ปฏิบัติงาน",
            doctorType: d.doctorPositionType || d.DoctorPositionType || "แพทย์ elective",
            birthDate: (d.birthDate || d.BirthDate || "").toString().split("T")[0],
            citizenId: d.citizenID || d.CitizenID || "",
            medicalLicenseNo: d.medicalLicenseNo || d.MedicalLicenseNo || "",
            department: d.departmentName || d.DepartmentName || "",
            dateRange: finalRange,
            startDate: sDate,
            endDate: eDate,
            fiscalYear: d.fiscalYear || d.FiscalYear || calculateThaiFiscalYear(sDate) || 2569,
            status: d.statusText || d.StatusText || (d.statusKey === 2 ? "กำลังฝึกอบรม" : (d.statusKey === 1 ? "รอเริ่มฝึก" : "สำเร็จการฝึกอบรม")),
            requiresAccommodation: hasDormStay,
            roomNumber: roomNumber,
            dormPaymentStatus: dormPaymentStatus,
            dormFee: matchedStay?.feeAmount ? `${matchedStay.feeAmount} บาท` : (d.doctorPositionType && d.doctorPositionType.includes("elective") ? "วันละ 100 บาท" : "500"),
            phone: d.phone || d.Phone || "",
            notes: d.notes || d.Notes || "",
            months: getAttendingMonths(d)
          };
        });
        if (isMountedRef.current) setDoctorList(mappedDocs);
      } else {
        if (isMountedRef.current) setDoctorList([]);
      }

      if (stuRes.status === "fulfilled" && stuRes.value?.data?.data && stuRes.value.data.data.length > 0) {
        const rawStus = stuRes.value.data.data;
        const mappedStus = rawStus.map(s => {
          let sDate = (s.startDate || s.StartDate || "").toString().split("T")[0];
          let eDate = (s.endDate || s.EndDate || "").toString().split("T")[0];
          const rText = s.dateRangeText || s.DateRangeText || "";
          if ((!sDate || !eDate) && rText) {
            const parsed = parseThaiDateRange(rText, s.fiscalYear || s.FiscalYear || 2569);
            if (!sDate && parsed.start) sDate = parsed.start;
            if (!eDate && parsed.end) eDate = parsed.end;
          }
          const finalRange = rText || formatThaiDateRangeText(sDate, eDate);

          const stuFullName = s.fullName || s.FullName || "";
          const cleanStuName = stuFullName.replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
          const matchedStay = activeStays.find(st => {
            const stName = (st.traineeName || st.TraineeName || "").replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
            return cleanStuName && stName && (cleanStuName === stName || cleanStuName.includes(stName) || stName.includes(cleanStuName));
          });

          const hasDormStay = Boolean(matchedStay || s.assignedRoomNumber || s.AssignedRoomNumber || s.requiresAccommodation || s.RequiresAccommodation);
          const roomNumber = matchedStay?.roomNumber || matchedStay?.RoomNumber || s.assignedRoomNumber || s.AssignedRoomNumber || (hasDormStay ? "รอระบุห้อง" : "ไม่ได้พัก");
          const dormPaymentStatus = matchedStay?.paymentStatus || matchedStay?.PaymentStatus || "PAID";

          return {
            id: s.studentID || s.StudentID,
            type: "STUDENT",
            bookNo: s.bookNo || s.BookNo || "",
            institution: s.institutionName || s.InstitutionName || "",
            batchCount: s.batchCount || s.BatchCount || 1,
            name: stuFullName,
            level: s.positionLevel || s.PositionLevel || "นักศึกษา",
            role: s.traineeRole || s.TraineeRole || "นศ.ฝึกงาน",
            major: s.majorField || s.MajorField || "",
            department: s.departmentName || s.DepartmentName || "",
            dateRange: finalRange,
            startDate: sDate,
            endDate: eDate,
            fiscalYear: s.fiscalYear || s.FiscalYear || calculateThaiFiscalYear(sDate) || 2569,
            status: s.statusText || s.StatusText || (s.statusKey === 2 ? "กำลังฝึกปฏิบัติงาน" : (s.statusKey === 1 ? "รอเริ่มฝึกงาน" : "สำเร็จการฝึกอบรม")),
            requiresAccommodation: hasDormStay,
            roomNumber: roomNumber,
            dormPaymentStatus: dormPaymentStatus,
            dormFee: matchedStay?.feeAmount ? `${matchedStay.feeAmount} บาท` : "500",
            phone: s.phone || s.Phone || "",
            notes: s.notes || s.Notes || "",
            months: getAttendingMonths(s)
          };
        });
        if (isMountedRef.current) setStudentList(mappedStus);
      } else {
        if (isMountedRef.current) setStudentList([]);
      }
    } catch (err) {
      console.warn("Could not load trainees from API, using default data", err);
      if (isMountedRef.current) {
        setDoctorList(DEFAULT_DOCTOR_LIST);
        setStudentList(DEFAULT_STUDENT_LIST);
      }
    } finally {
      if (isMountedRef.current) setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchTraineeData();
    axios.get("/api/v1/trainee/departments")
      .then(res => {
        if (!isMountedRef.current) return;
        if (res.data && res.data.success && res.data.data && res.data.data.length > 0) {
          const names = res.data.data.map(d => d.name).filter(Boolean);
          if (names.length > 0 && isMountedRef.current) {
            setDepartmentsList(names);
          }
        }
      })
      .catch(err => {
        console.warn("Could not load departments from API, using default", err);
      });
  }, []);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  // Modals
  const [viewModal, setViewModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formModal, setFormModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formType, setFormType] = useState("DOCTOR"); // "DOCTOR" หรือ "STUDENT"

  // Form State
  const [formData, setFormData] = useState({
    id: "",
    bookNo: "",
    institution: "",
    batchCount: 1,
    nameTH: "",
    nameEN: "",
    position: "",
    doctorType: "แพทย์ elective",
    birthDate: "",
    citizenId: "",
    medicalLicenseNo: "",
    level: "",
    role: "",
    major: "",
    department: "กลุ่มงานพยาบาลผู้ป่วยนอก",
    startDate: "",
    endDate: "",
    dateRange: "",
    fiscalYear: 2569,
    status: "กำลังฝึกอบรม",
    requiresAccommodation: false,
    roomNumber: "ไม่ได้พัก",
    dormFee: "500",
    phone: "",
    notes: ""
  });

  // Calculate Thai Fiscal Year & DateRange on Date change
  const handleStartDateChange = (dateVal) => {
    const calcYear = calculateThaiFiscalYear(dateVal);
    setFormData((prev) => {
      const autoRange = formatThaiDateRangeText(dateVal, prev.endDate);
      return {
        ...prev,
        startDate: dateVal,
        dateRange: autoRange || prev.dateRange,
        fiscalYear: calcYear || prev.fiscalYear
      };
    });
  };

  const handleEndDateChange = (dateVal) => {
    setFormData((prev) => {
      const autoRange = formatThaiDateRangeText(prev.startDate, dateVal);
      return {
        ...prev,
        endDate: dateVal,
        dateRange: autoRange || prev.dateRange
      };
    });
  };

  // Open Add Modal
  const handleOpenAddModal = (type) => {
    setIsEditing(false);
    setFormType(type || (activeTab === 0 ? "DOCTOR" : "STUDENT"));
    const today = new Date().toISOString().split("T")[0];
    const initialRange = formatThaiDateRangeText(today, "");
    setFormData({
      id: Date.now().toString(),
      bookNo: "",
      institution: "",
      batchCount: 1,
      nameTH: "",
      nameEN: "",
      position: type === "DOCTOR" ? "แพทย์ปฏิบัติการชั้นปีที่ 1" : "นศ.ปี 4",
      doctorType: "แพทย์ fellow",
      birthDate: "",
      citizenId: "",
      medicalLicenseNo: "",
      level: "ปี 4",
      role: "นศ.ห้องเภสัชกรรม",
      major: "",
      department: "กลุ่มงานพยาบาลผู้ป่วยนอก",
      startDate: today,
      endDate: "",
      dateRange: initialRange,
      fiscalYear: calculateThaiFiscalYear(today) || 2569,
      status: "กำลังฝึกปฏิบัติงาน",
      requiresAccommodation: false,
      building: "ตึก 1",
      floor: 1,
      roomNumber: "ไม่ได้พัก",
      dormFee: "500",
      phone: "",
      notes: ""
    });
    setFormModal(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (item) => {
    setIsEditing(true);
    setFormType(item.type);

    let start = item.startDate ? String(item.startDate).split("T")[0] : "";
    let end = item.endDate ? String(item.endDate).split("T")[0] : "";
    const rangeText = item.dateRange || item.dateRangeText || "";

    if ((!start || !end) && rangeText) {
      const parsed = parseThaiDateRange(rangeText, item.fiscalYear || 2569);
      if (!start && parsed.start) start = parsed.start;
      if (!end && parsed.end) end = parsed.end;
    }

    const finalRange = rangeText || formatThaiDateRangeText(start, end);

    setFormData({
      ...item,
      startDate: start,
      endDate: end,
      dateRange: finalRange,
      nameTH: item.nameTH || item.name || "",
      nameEN: item.nameEN || "",
      doctorType: item.doctorType || "แพทย์ elective",
      medicalLicenseNo: item.medicalLicenseNo || "",
      citizenId: item.citizenId || "",
      birthDate: item.birthDate ? String(item.birthDate).split("T")[0] : "",
      fiscalYear: item.fiscalYear || calculateThaiFiscalYear(start) || 2569,
      requiresAccommodation: item.requiresAccommodation ?? false,
      building: item.building || "ตึก 1",
      floor: item.floor || 1,
      roomNumber: item.roomNumber || "ไม่ได้พัก"
    });
    setFormModal(true);
  };

  // Save Form
  const handleSaveForm = () => {
    const finalDateRange = formData.dateRange || formatThaiDateRangeText(formData.startDate, formData.endDate);
    const finalFiscalYear = Number(formData.fiscalYear) || calculateThaiFiscalYear(formData.startDate) || 2569;

    if (formType === "DOCTOR") {
      const docItem = {
        ...formData,
        startDate: formData.startDate,
        endDate: formData.endDate,
        dateRange: finalDateRange,
        type: "DOCTOR",
        nameTH: formData.nameTH || "แพทย์ไม่ระบุชื่อ",
        name: formData.nameTH || "แพทย์ไม่ระบุชื่อ",
        fiscalYear: finalFiscalYear,
        months: formData.months || ["ต.ค."]
      };
      if (isEditing) {
        setDoctorList((prev) => prev.map((d) => (d.id === docItem.id ? docItem : d)));
      } else {
        setDoctorList((prev) => [docItem, ...prev]);
      }
    } else {
      const studentItem = {
        ...formData,
        startDate: formData.startDate,
        endDate: formData.endDate,
        dateRange: finalDateRange,
        type: "STUDENT",
        name: formData.nameTH || formData.name || "นักศึกษาไม่ระบุชื่อ",
        fiscalYear: finalFiscalYear,
        months: formData.months || ["ต.ค."]
      };
      if (isEditing) {
        setStudentList((prev) => prev.map((s) => (s.id === studentItem.id ? studentItem : s)));
      } else {
        setStudentList((prev) => [studentItem, ...prev]);
      }
    }
    setFormModal(false);
  };

  // Delete Item
  const handleDeleteItem = (id, type) => {
    if (window.confirm("คุณต้องการลบข้อมูลรายการนี้ใช่หรือไม่?")) {
      if (type === "DOCTOR") {
        setDoctorList((prev) => prev.filter((d) => d.id !== id));
      } else {
        setStudentList((prev) => prev.filter((s) => s.id !== id));
      }
    }
  };

  // Helper: คำนวณสถานะการฝึกอบรม (ACTIVE = กำลังฝึกปัจจุบัน, UPCOMING = รอเริ่มฝึก, PAST = สำเร็จการฝึก/พ้นกำหนด)
  const getTraineeStatus = useCallback((item) => {
    if (!item) return { key: "PAST", label: "สำเร็จการฝึก", color: "secondary" };
    const now = new Date();

    // 1. ตรวจจาก startDate และ endDate ถ้ามี
    let start = null;
    let end = null;

    if (item.startDate) {
      const s = new Date(item.startDate);
      if (!isNaN(s.getTime())) {
        start = s;
        start.setHours(0, 0, 0, 0);
      }
    }
    if (item.endDate) {
      const e = new Date(item.endDate);
      if (!isNaN(e.getTime())) {
        end = e;
        end.setHours(23, 59, 59, 999);
      }
    }

    // ถ้าไม่มี endDate จาก ISO ให้ลอง parse จาก dateRange
    if (!end && item.dateRange) {
      const parsedEnd = parseThaiEndDate(item.dateRange, item.fiscalYear);
      if (parsedEnd) {
        end = parsedEnd;
      }
    }

    if (start && end) {
      if (now < start) return { key: "UPCOMING", label: "รอเริ่มฝึก", color: "info" };
      if (now > end) return { key: "PAST", label: "สำเร็จการฝึก", color: "secondary" };
      return { key: "ACTIVE", label: "กำลังฝึกปฏิบัติงาน", color: "success" };
    } else if (start && !end) {
      if (now < start) return { key: "UPCOMING", label: "รอเริ่มฝึก", color: "info" };
      return { key: "ACTIVE", label: "กำลังฝึกปฏิบัติงาน", color: "success" };
    }

    // 2. Fallback จาก status string ถ้าไม่มีวันที่
    const st = (item.status || "").trim();
    if (st.includes("สำเร็จ") || st.includes("เสร็จ") || st.includes("ออกไปแล้ว") || st.includes("พ้น")) {
      return { key: "PAST", label: "สำเร็จการฝึก", color: "secondary" };
    }
    if (st.includes("รอ") || st.includes("ยังไม่")) {
      return { key: "UPCOMING", label: "รอเริ่มฝึก", color: "info" };
    }
    return { key: "ACTIVE", label: "กำลังฝึกปฏิบัติงาน", color: "success" };
  }, []);

  // 1. กรองข้อมูลแพทย์เบื้องต้น (ตามคำค้นหา, แผนก, ปีงบประมาณ, หอพัก)
  const doctorBaseList = useMemo(() => {
    return doctorList.filter((doc) => {
      const matchSearch =
        searchTerm === "" ||
        (doc.nameTH && doc.nameTH.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (doc.nameEN && doc.nameEN.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (doc.bookNo && doc.bookNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (doc.institution && doc.institution.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (doc.medicalLicenseNo && doc.medicalLicenseNo.includes(searchTerm));

      const matchDept = selectedDepartment === "ALL" || doc.department === selectedDepartment;
      const matchFiscal = selectedFiscalYear === "ALL" || doc.fiscalYear.toString() === selectedFiscalYear;
      const matchDorm =
        selectedDormStatus === "ALL" ||
        (selectedDormStatus === "YES" && doc.requiresAccommodation) ||
        (selectedDormStatus === "NO" && !doc.requiresAccommodation);

      return matchSearch && matchDept && matchFiscal && matchDorm;
    });
  }, [doctorList, searchTerm, selectedDepartment, selectedFiscalYear, selectedDormStatus]);

  // 2. กรองข้อมูลนักศึกษาเบื้องต้น (ตามคำค้นหา, แผนก, ปีงบประมาณ, หอพัก)
  const studentBaseList = useMemo(() => {
    return studentList.filter((std) => {
      const matchSearch =
        searchTerm === "" ||
        (std.name && std.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (std.bookNo && std.bookNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (std.institution && std.institution.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (std.major && std.major.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (std.role && std.role.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchDept = selectedDepartment === "ALL" || std.department === selectedDepartment;
      const matchFiscal = selectedFiscalYear === "ALL" || std.fiscalYear.toString() === selectedFiscalYear;
      const matchDorm =
        selectedDormStatus === "ALL" ||
        (selectedDormStatus === "YES" && std.requiresAccommodation) ||
        (selectedDormStatus === "NO" && !std.requiresAccommodation);

      return matchSearch && matchDept && matchFiscal && matchDorm;
    });
  }, [studentList, searchTerm, selectedDepartment, selectedFiscalYear, selectedDormStatus]);

  // เดือนปัจจุบันตามปฏิทินไทย
  const currentThaiMonth = useMemo(() => {
    const now = new Date();
    const MONTH_NAMES = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
    return MONTH_NAMES[now.getMonth()];
  }, []);

  // นับจำนวนสถานะสำหรับแท็บที่เปิดอยู่
  const currentTabBaseList = useMemo(() => {
    if (activeTab === 0) return doctorBaseList;
    if (activeTab === 1) return studentBaseList;
    if (matrixTypeFilter === "DOCTOR") return doctorBaseList;
    if (matrixTypeFilter === "STUDENT") return studentBaseList;
    return [...doctorBaseList, ...studentBaseList];
  }, [activeTab, matrixTypeFilter, doctorBaseList, studentBaseList]);

  const statusCounts = useMemo(() => {
    let active = 0;
    let upcoming = 0;
    let past = 0;

    currentTabBaseList.forEach(item => {
      const st = getTraineeStatus(item).key;
      if (st === "ACTIVE") active++;
      else if (st === "UPCOMING") upcoming++;
      else if (st === "PAST") past++;
    });

    return {
      active,
      upcoming,
      past,
      total: currentTabBaseList.length
    };
  }, [currentTabBaseList, getTraineeStatus]);

  // กรองแพทย์ตามสถานะการฝึกอบรม
  const filteredDoctors = useMemo(() => {
    if (trainingStatusFilter === "ALL") return doctorBaseList;
    return doctorBaseList.filter(d => getTraineeStatus(d).key === trainingStatusFilter);
  }, [doctorBaseList, trainingStatusFilter, getTraineeStatus]);

  // กรองนักศึกษาตามสถานะการฝึกปฏิบัติงาน
  const filteredStudents = useMemo(() => {
    if (trainingStatusFilter === "ALL") return studentBaseList;
    return studentBaseList.filter(s => getTraineeStatus(s).key === trainingStatusFilter);
  }, [studentBaseList, trainingStatusFilter, getTraineeStatus]);

  // สลับแท็บพร้อมปรับตัวกรองสถานะให้เหมาะสม (ไม่ติดผลลัพธ์ว่าง)
  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);

    if (newTab === 0) {
      if (trainingStatusFilter !== "ALL") {
        const hasMatch = doctorBaseList.some(d => getTraineeStatus(d).key === trainingStatusFilter);
        if (!hasMatch) {
          const hasActive = doctorBaseList.some(d => getTraineeStatus(d).key === "ACTIVE");
          setTrainingStatusFilter(hasActive ? "ACTIVE" : "ALL");
        }
      }
    } else if (newTab === 1) {
      if (trainingStatusFilter !== "ALL") {
        const hasMatch = studentBaseList.some(s => getTraineeStatus(s).key === trainingStatusFilter);
        if (!hasMatch) {
          const hasActive = studentBaseList.some(s => getTraineeStatus(s).key === "ACTIVE");
          setTrainingStatusFilter(hasActive ? "ACTIVE" : "ALL");
        }
      }
    } else if (newTab === 2) {
      if (trainingStatusFilter !== "ALL" && trainingStatusFilter !== "ACTIVE") {
        setTrainingStatusFilter("ALL");
      }
      setMatrixMonthFilter("ALL");
    }
  }, [doctorBaseList, studentBaseList, trainingStatusFilter, getTraineeStatus]);

  // สถิติยอดรวมผู้ฝึกแยกรายเดือน 12 เดือน (สำหรับ Ribbon สรุป และ Footer ท้ายตาราง Matrix)
  const monthlyStats = useMemo(() => {
    const stats = {};
    MONTH_COLS.forEach(col => {
      stats[col.key] = {
        total: 0,
        doctors: 0,
        students: 0
      };
    });

    const targetDocs = (matrixTypeFilter === "ALL" || matrixTypeFilter === "DOCTOR") ? filteredDoctors : [];
    const targetStus = (matrixTypeFilter === "ALL" || matrixTypeFilter === "STUDENT") ? filteredStudents : [];

    targetDocs.forEach(d => {
      const m = d.months || getAttendingMonths(d);
      m.forEach(colKey => {
        if (stats[colKey]) {
          stats[colKey].doctors++;
          stats[colKey].total++;
        }
      });
    });

    targetStus.forEach(s => {
      const m = s.months || getAttendingMonths(s);
      m.forEach(colKey => {
        if (stats[colKey]) {
          stats[colKey].students++;
          stats[colKey].total++;
        }
      });
    });

    let maxCount = 0;
    let peakMonth = "";
    Object.entries(stats).forEach(([mKey, data]) => {
      if (data.total > maxCount) {
        maxCount = data.total;
        peakMonth = mKey;
      }
    });

    return { stats, maxCount, peakMonth };
  }, [filteredDoctors, filteredStudents, matrixTypeFilter]);

  // รายการผู้ฝึกงานที่จะแสดงใน Tab 2 (ตาม matrixTypeFilter และ matrixMonthFilter)
  const matrixDataList = useMemo(() => {
    let list = [];
    if (matrixTypeFilter === "DOCTOR") {
      list = filteredDoctors;
    } else if (matrixTypeFilter === "STUDENT") {
      list = filteredStudents;
    } else {
      list = [...filteredDoctors, ...filteredStudents];
    }

    if (matrixMonthFilter !== "ALL") {
      list = list.filter(item => {
        const m = item.months || getAttendingMonths(item);
        return m.includes(matrixMonthFilter);
      });
    }

    return list;
  }, [filteredDoctors, filteredStudents, matrixTypeFilter, matrixMonthFilter]);

  // Statistics
  const totalDoctors = doctorList.length;
  const totalStudents = studentList.length;
  const totalDormStayers =
    doctorList.filter((d) => d.requiresAccommodation).length +
    studentList.filter((s) => s.requiresAccommodation).length;
  const currentFiscalYearActive =
    doctorList.filter((d) => String(d.fiscalYear) === currentFiscalYearStr).length +
    studentList.filter((s) => String(s.fiscalYear) === currentFiscalYearStr).length;

  // Active list based on tab
  const activeList = activeTab === 0 ? filteredDoctors : filteredStudents;
  const totalPages = Math.ceil(activeList.length / pageSize) || 1;
  const paginatedList = activeList.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Status Badge Helper
  const renderStatusBadge = (item) => {
    const stObj = typeof item === "object" && item !== null
      ? getTraineeStatus(item)
      : { key: (item || "").includes("กำลัง") ? "ACTIVE" : ((item || "").includes("รอ") ? "UPCOMING" : "PAST"), label: item };

    if (stObj.key === "ACTIVE") {
      return (
        <span style={{ backgroundColor: "#dcfce7", color: "#15803d", border: "1px solid #86efac", padding: "4px 9px", borderRadius: "6px", fontSize: "0.78rem", fontWeight: "800", display: "inline-block" }}>
          🟢 กำลังฝึกปฏิบัติงาน
        </span>
      );
    } else if (stObj.key === "UPCOMING") {
      return (
        <span style={{ backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #7dd3fc", padding: "4px 9px", borderRadius: "6px", fontSize: "0.78rem", fontWeight: "800", display: "inline-block" }}>
          ⏳ รอเริ่มฝึก
        </span>
      );
    } else {
      return (
        <span style={{ backgroundColor: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", padding: "4px 9px", borderRadius: "6px", fontSize: "0.78rem", fontWeight: "800", display: "inline-block" }}>
          🚪 สำเร็จการฝึก
        </span>
      );
    }
  };

  // Accommodation Badge Helper
  const renderRoomBadge = (requires, roomNo, paymentStatus) => {
    if (!requires || roomNo === "ไม่ได้พัก") {
      return <Badge color="light" style={{ color: "#666", border: "1px solid #ccc", fontSize: "0.8rem" }}>ไม่ได้พัก</Badge>;
    }
    const isOverdue = paymentStatus === "OVERDUE";
    return (
      <Badge
        color={isOverdue ? "danger" : "info"}
        style={{ fontSize: "0.8rem", padding: "5px 8px", cursor: "pointer" }}
        title={isOverdue ? "ค้างชำระค่าหอพัก" : "พักหอพัก รพ."}
      >
        <HotelIcon style={{ fontSize: "0.9rem", verticalAlign: "middle", marginRight: "3px" }} />
        ห้อง {roomNo} {isOverdue && "(ค้างชำระ)"}
      </Badge>
    );
  };

  return (
    <div className={classes.container}>
      {/* Main Container Card: Dedicated to Data Management */}
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="info" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", backgroundColor: "#00838f" }}>
              <div>
                <h4 style={{ color: "#fff", margin: 0, fontWeight: "bold" }}>
                  ระบบบริหารจัดการข้อมูลแพทย์และนักศึกษาฝึกงาน
                </h4>
                <p style={{ color: "rgba(255,255,255,0.85)", margin: "4px 0 0 0", fontSize: "0.88rem" }}>
                  โรงพยาบาลโรคผิวหนังเขตร้อนภาคใต้ จังหวัดตรัง (คำนวณปีงบประมาณไทย 1 ต.ค. - 30 ก.ย.)
                </p>
              </div>
              <div style={{ marginTop: "8px" }}>
                <Button
                  color="white"
                  size="sm"
                  style={{ marginRight: "10px", fontWeight: "bold", color: "#00695c" }}
                  onClick={() => handleOpenAddModal(activeTab === 0 ? "DOCTOR" : "STUDENT")}
                >
                  <AddIcon style={{ fontSize: "1.1rem", verticalAlign: "middle", marginRight: "4px" }} />
                  {activeTab === 0 ? "+ เพิ่มข้อมูลแพทย์" : "+ เพิ่มข้อมูลนักศึกษา"}
                </Button>
              </div>
            </CardHeader>

            {/* Navigation Tabs */}
            <div style={{ borderBottom: "2px solid #e0e0e0", backgroundColor: "#fafafa", padding: "10px 15px 0 15px" }}>
              <Nav tabs style={{ borderBottom: "none" }}>
                <NavItem>
                  <NavLink
                    style={{
                      cursor: "pointer",
                      fontWeight: activeTab === 0 ? "bold" : "normal",
                      color: activeTab === 0 ? "#00838f" : "#555",
                      borderBottom: activeTab === 0 ? "3px solid #00838f" : "none",
                      backgroundColor: activeTab === 0 ? "#fff" : "transparent",
                      fontSize: "1rem",
                      padding: "10px 20px"
                    }}
                    onClick={() => handleTabChange(0)}
                  >
                    <LocalHospitalIcon style={{ verticalAlign: "middle", marginRight: "6px", fontSize: "1.2rem" }} />
                    ข้อมูลแพทย์ฝึกอบรม / ดูงาน ({doctorBaseList.length})
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    style={{
                      cursor: "pointer",
                      fontWeight: activeTab === 1 ? "bold" : "normal",
                      color: activeTab === 1 ? "#2e7d32" : "#555",
                      borderBottom: activeTab === 1 ? "3px solid #2e7d32" : "none",
                      backgroundColor: activeTab === 1 ? "#fff" : "transparent",
                      fontSize: "1rem",
                      padding: "10px 20px"
                    }}
                    onClick={() => handleTabChange(1)}
                  >
                    <SchoolIcon style={{ verticalAlign: "middle", marginRight: "6px", fontSize: "1.2rem" }} />
                    ข้อมูลนักศึกษาฝึกงาน ({studentBaseList.length})
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    style={{
                      cursor: "pointer",
                      fontWeight: activeTab === 2 ? "bold" : "normal",
                      color: activeTab === 2 ? "#e65100" : "#555",
                      borderBottom: activeTab === 2 ? "3px solid #e65100" : "none",
                      backgroundColor: activeTab === 2 ? "#fff" : "transparent",
                      fontSize: "1rem",
                      padding: "10px 20px"
                    }}
                    onClick={() => handleTabChange(2)}
                  >
                    <TableChartIcon style={{ verticalAlign: "middle", marginRight: "6px", fontSize: "1.2rem" }} />
                    ตารางรายเดือน Matrix ปฏิทินการฝึก ({doctorBaseList.length + studentBaseList.length})
                  </NavLink>
                </NavItem>
              </Nav>
            </div>

            <CardBody>
              {/* แถบตัวกรองสถานะ: กำลังฝึกอยู่ปัจจุบัน vs รอเริ่มฝึก vs ที่ผ่านมาแล้ว vs ทั้งหมด */}
              <div
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "12px 18px",
                  marginBottom: "16px",
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }}
              >
                <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                  <span style={{ fontSize: "0.9rem", fontWeight: "800", color: "#1e293b", marginRight: "4px" }}>
                    สถานะการฝึก:
                  </span>
                  <div className="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      onClick={() => { setTrainingStatusFilter("ACTIVE"); setCurrentPage(1); }}
                      style={{
                        backgroundColor: trainingStatusFilter === "ACTIVE" ? "#16a34a" : "#ffffff",
                        color: trainingStatusFilter === "ACTIVE" ? "#ffffff" : "#15803d",
                        borderColor: trainingStatusFilter === "ACTIVE" ? "#16a34a" : "#86efac",
                        fontWeight: "800",
                        fontSize: "0.83rem",
                        padding: "6px 14px",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                      className="btn btn-outline-success"
                    >
                      <span>🟢 กำลังฝึกอยู่ปัจจุบัน</span>
                      <span style={{
                        backgroundColor: trainingStatusFilter === "ACTIVE" ? "rgba(255,255,255,0.3)" : "#dcfce7",
                        color: trainingStatusFilter === "ACTIVE" ? "#ffffff" : "#166534",
                        padding: "1px 7px",
                        borderRadius: "10px",
                        fontSize: "0.75rem",
                        fontWeight: "800"
                      }}>
                        {statusCounts.active}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setTrainingStatusFilter("UPCOMING"); setCurrentPage(1); }}
                      style={{
                        backgroundColor: trainingStatusFilter === "UPCOMING" ? "#0284c7" : "#ffffff",
                        color: trainingStatusFilter === "UPCOMING" ? "#ffffff" : "#0369a1",
                        borderColor: trainingStatusFilter === "UPCOMING" ? "#0284c7" : "#7dd3fc",
                        fontWeight: "800",
                        fontSize: "0.83rem",
                        padding: "6px 14px",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                      className="btn btn-outline-info"
                    >
                      <span>⏳ รอเริ่มฝึก (เร็วๆ นี้)</span>
                      <span style={{
                        backgroundColor: trainingStatusFilter === "UPCOMING" ? "rgba(255,255,255,0.3)" : "#e0f2fe",
                        color: trainingStatusFilter === "UPCOMING" ? "#ffffff" : "#0369a1",
                        padding: "1px 7px",
                        borderRadius: "10px",
                        fontSize: "0.75rem",
                        fontWeight: "800"
                      }}>
                        {statusCounts.upcoming}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setTrainingStatusFilter("PAST"); setCurrentPage(1); }}
                      style={{
                        backgroundColor: trainingStatusFilter === "PAST" ? "#475569" : "#ffffff",
                        color: trainingStatusFilter === "PAST" ? "#ffffff" : "#475569",
                        borderColor: trainingStatusFilter === "PAST" ? "#475569" : "#cbd5e1",
                        fontWeight: "800",
                        fontSize: "0.83rem",
                        padding: "6px 14px",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                      className="btn btn-outline-secondary"
                    >
                      <span>🚪 ที่ผ่านมาแล้ว (ดูประวัติ)</span>
                      <span style={{
                        backgroundColor: trainingStatusFilter === "PAST" ? "rgba(255,255,255,0.3)" : "#f1f5f9",
                        color: trainingStatusFilter === "PAST" ? "#ffffff" : "#334155",
                        padding: "1px 7px",
                        borderRadius: "10px",
                        fontSize: "0.75rem",
                        fontWeight: "800"
                      }}>
                        {statusCounts.past}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => { setTrainingStatusFilter("ALL"); setCurrentPage(1); }}
                      style={{
                        backgroundColor: trainingStatusFilter === "ALL" ? "#0f172a" : "#ffffff",
                        color: trainingStatusFilter === "ALL" ? "#ffffff" : "#334155",
                        borderColor: "#cbd5e1",
                        fontWeight: "800",
                        fontSize: "0.83rem",
                        padding: "6px 14px",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                      className="btn btn-outline-dark"
                    >
                      <span>📋 ทั้งหมด</span>
                      <span style={{
                        backgroundColor: trainingStatusFilter === "ALL" ? "rgba(255,255,255,0.3)" : "#e2e8f0",
                        color: trainingStatusFilter === "ALL" ? "#ffffff" : "#334155",
                        padding: "1px 7px",
                        borderRadius: "10px",
                        fontSize: "0.75rem",
                        fontWeight: "800"
                      }}>
                        {statusCounts.total}
                      </span>
                    </button>
                  </div>
                </div>

                <div style={{ fontSize: "0.84rem", color: "#64748b", fontWeight: "600" }}>
                  {trainingStatusFilter === "ACTIVE" && "⚡ แสดงเฉพาะผู้ที่กำลังฝึกปฏิบัติงานอยู่ในช่วงเวลานี้"}
                  {trainingStatusFilter === "UPCOMING" && "📅 แสดงผู้ที่มีกำหนดฝึกในรอบถัดไป (ยังไม่ถึงวันเริ่ม)"}
                  {trainingStatusFilter === "PAST" && "📁 แสดงประวัติผู้ที่พ้นกำหนดฝึกปฏิบัติงานไปแล้ว"}
                  {trainingStatusFilter === "ALL" && "📋 แสดงผู้ฝึกงานทุกสถานะตามเงื่อนไขตัวกรอง"}
                </div>
              </div>

              {/* Search & Filter Toolbar */}
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "12px 15px",
                  borderRadius: "6px",
                  marginBottom: "20px",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  alignItems: "center"
                }}
              >
                {/* Search input */}
                <div style={{ flex: "1 1 240px", minWidth: "220px", position: "relative" }}>
                  <SearchIcon style={{ position: "absolute", left: "10px", top: "10px", color: "#888" }} />
                  <input
                    type="text"
                    className="form-control"
                    style={{ paddingLeft: "36px", fontSize: "0.9rem" }}
                    placeholder={
                      activeTab === 0
                        ? "ค้นหาชื่อแพทย์, เลข ว., รพ.ต้นสังกัด..."
                        : activeTab === 1
                        ? "ค้นหาชื่อนักศึกษา, มหาวิทยาลัย, สาขา..."
                        : matrixTypeFilter === "DOCTOR"
                        ? "ค้นหาชื่อแพทย์, สถาบัน..."
                        : matrixTypeFilter === "STUDENT"
                        ? "ค้นหาชื่อนักศึกษา, มหาวิทยาลัย..."
                        : "ค้นหาชื่อแพทย์ หรือ นักศึกษา, สถาบัน..."
                    }
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  />
                </div>

                {/* If Matrix Tab (Tab 2), Show Type Filter Dropdown */}
                {activeTab === 2 && (
                  <div style={{ minWidth: "220px" }}>
                    <select
                      className="form-control"
                      style={{ fontSize: "0.9rem", fontWeight: "600", borderColor: "#0288d1" }}
                      value={matrixTypeFilter}
                      onChange={(e) => setMatrixTypeFilter(e.target.value)}
                    >
                      <option value="ALL">👥 แสดงทั้งหมด (แพทย์ + นศ.)</option>
                      <option value="DOCTOR">👨‍⚕️ แสดงเฉพาะแพทย์ฝึกอบรม ({filteredDoctors.length} คน)</option>
                      <option value="STUDENT">🎓 แสดงเฉพาะนักศึกษาฝึกงาน ({filteredStudents.length} คน)</option>
                    </select>
                  </div>
                )}

                {/* Fiscal Year Filter (คำนวณ 1 ต.ค. - 30 ก.ย.) */}
                <div style={{ minWidth: "200px" }}>
                  <select
                    className="form-control"
                    style={{ fontSize: "0.9rem", fontWeight: "600" }}
                    value={selectedFiscalYear}
                    onChange={(e) => handleFiscalYearChange(e.target.value)}
                  >
                    <option value={currentFiscalYearStr}>📅 ปีงบประมาณ {currentFiscalYearStr} (ปีปัจจุบัน)</option>
                    <option value="ALL">📅 ทุกปีงบประมาณ</option>
                    <option value={String(Number(currentFiscalYearStr) + 1)}>ปีงบประมาณ {Number(currentFiscalYearStr) + 1} (ล่วงหน้า)</option>
                    <option value={String(Number(currentFiscalYearStr) - 1)}>ปีงบประมาณ {Number(currentFiscalYearStr) - 1} (ย้อนหลัง)</option>
                    <option value={String(Number(currentFiscalYearStr) - 2)}>ปีงบประมาณ {Number(currentFiscalYearStr) - 2} (ย้อนหลัง 2 ปี)</option>
                  </select>
                </div>

                {/* Department Filter (เชื่อมโยงฐานข้อมูล รพ.) */}
                <div style={{ minWidth: "180px" }}>
                  <select
                    className="form-control"
                    style={{ fontSize: "0.9rem" }}
                    value={selectedDepartment}
                    onChange={(e) => { setSelectedDepartment(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="ALL">🏢 ทุกกลุ่มงานที่ฝึก</option>
                    {departmentsList.map((deptName) => (
                      <option key={deptName} value={deptName}>
                        {deptName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dormitory Accommodation Filter */}
                <div style={{ minWidth: "150px" }}>
                  <select
                    className="form-control"
                    style={{ fontSize: "0.9rem" }}
                    value={selectedDormStatus}
                    onChange={(e) => { setSelectedDormStatus(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="ALL">🏠 ทุกการขอห้องพัก</option>
                    <option value="YES">พักหอพัก รพ.</option>
                    <option value="NO">ไม่ได้พัก</option>
                  </select>
                </div>

                {(searchTerm || selectedDepartment !== "ALL" || selectedFiscalYear !== currentFiscalYearStr || selectedDormStatus !== "ALL" || trainingStatusFilter !== "ACTIVE" || (activeTab === 2 && (matrixTypeFilter !== "ALL" || matrixMonthFilter !== "ALL"))) && (
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedDepartment("ALL");
                      setSelectedFiscalYear(currentFiscalYearStr);
                      setSelectedDormStatus("ALL");
                      setTrainingStatusFilter("ACTIVE");
                      setMatrixTypeFilter("ALL");
                      setMatrixMonthFilter("ALL");
                      setCurrentPage(1);
                    }}
                  >
                    ล้างตัวกรอง
                  </Button>
                )}
              </div>

              {/* TAB 0: ข้อมูลแพทย์ฝึกอบรม / ศึกษาดูงาน */}
              {activeTab === 0 && (
                <div>
                  <div style={{ overflowX: "auto" }}>
                    <table className={classes.table}>
                      <thead>
                        <tr>
                          <th className={classes.thCenter} style={{ width: "50px" }}>ลำดับ</th>
                          <th className={classes.th}>เลขที่หนังสือ / ต้นสังกัด</th>
                          <th className={classes.th}>ชื่อ-สกุลแพทย์ (ไทย / English)</th>
                          <th className={classes.th}>ตำแหน่ง / ประเภท</th>
                          <th className={classes.thCenter}>เลข ว. (ใบประกอบฯ)</th>
                          <th className={classes.th}>กลุ่มงานที่ฝึก</th>
                          <th className={classes.th}>ช่วงเวลาฝึกปฏิบัติ</th>
                          <th className={classes.thCenter}>ปีงบ</th>
                          <th className={classes.thCenter}>ห้องพัก</th>
                          <th className={classes.thCenter}>สถานะ</th>
                          <th className={classes.thCenter} style={{ width: "120px" }}>จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedList.length === 0 ? (
                          <tr>
                            <td colSpan="11" style={{ textAlign: "center", padding: "30px", color: "#888" }}>
                              ไม่พบข้อมูลแพทย์ตามเงื่อนไขที่ค้นหา
                            </td>
                          </tr>
                        ) : (
                          paginatedList.map((doc, idx) => (
                            <tr key={doc.id} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fafcff" }}>
                              <td className={classes.td} style={{ textAlign: "center", fontWeight: "bold", color: "#666" }}>
                                {(currentPage - 1) * pageSize + idx + 1}
                              </td>
                              <td className={classes.td}>
                                <div style={{ fontWeight: "600", color: "#1976d2", fontSize: "0.85rem" }}>
                                  {doc.bookNo || "-"}
                                </div>
                                <div style={{ fontSize: "0.82rem", color: "#555" }}>
                                  {doc.institution}
                                </div>
                              </td>
                              <td className={classes.td}>
                                <div style={{ fontWeight: "bold", color: "#222" }}>
                                  {doc.nameTH}
                                </div>
                                {doc.nameEN && (
                                  <div style={{ fontSize: "0.8rem", color: "#777", fontStyle: "italic" }}>
                                    {doc.nameEN}
                                  </div>
                                )}
                              </td>
                              <td className={classes.td}>
                                <div style={{ fontSize: "0.85rem" }}>{doc.position}</div>
                                <Badge color="warning" style={{ color: "#333", fontSize: "0.75rem", marginTop: "2px" }}>
                                  {doc.doctorType}
                                </Badge>
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                {doc.medicalLicenseNo && doc.medicalLicenseNo !== "0" ? (
                                  <span style={{ fontFamily: "monospace", fontWeight: "bold", color: "#00695c", backgroundColor: "#e0f2f1", padding: "2px 6px", borderRadius: "4px" }}>
                                    ว.{doc.medicalLicenseNo}
                                  </span>
                                ) : (
                                  <span style={{ color: "#999" }}>-</span>
                                )}
                              </td>
                              <td className={classes.td}>
                                <span style={{ fontSize: "0.85rem", color: "#333" }}>{doc.department}</span>
                              </td>
                              <td className={classes.td}>
                                <div style={{ fontSize: "0.85rem", fontWeight: "500" }}>{doc.dateRange}</div>
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                <Badge color="dark" style={{ fontSize: "0.8rem", padding: "4px 7px" }}>
                                  {doc.fiscalYear}
                                </Badge>
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                {renderRoomBadge(doc.requiresAccommodation, doc.roomNumber, doc.dormPaymentStatus)}
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                {renderStatusBadge(doc)}
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                <Button
                                  color="info"
                                  simple
                                  justIcon
                                  size="sm"
                                  title="ดูรายละเอียด"
                                  onClick={() => { setSelectedItem(doc); setViewModal(true); }}
                                >
                                  <VisibilityIcon style={{ fontSize: "1.1rem" }} />
                                </Button>
                                <Button
                                  color="warning"
                                  simple
                                  justIcon
                                  size="sm"
                                  title="แก้ไข"
                                  onClick={() => handleOpenEditModal(doc)}
                                >
                                  <EditIcon style={{ fontSize: "1.1rem" }} />
                                </Button>
                                <Button
                                  color="danger"
                                  simple
                                  justIcon
                                  size="sm"
                                  title="ลบ"
                                  onClick={() => handleDeleteItem(doc.id, "DOCTOR")}
                                >
                                  <DeleteIcon style={{ fontSize: "1.1rem" }} />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* TAB 1: ข้อมูลนักศึกษาฝึกงาน */}
              {activeTab === 1 && (
                <div>
                  <div style={{ overflowX: "auto" }}>
                    <table className={classes.table}>
                      <thead>
                        <tr>
                          <th className={classes.thCenter} style={{ width: "50px" }}>ลำดับ</th>
                          <th className={classes.th}>เลขที่หนังสือ / สถาบัน</th>
                          <th className={classes.thCenter}>จำนวน</th>
                          <th className={classes.th}>ชื่อ-สกุล นักศึกษา</th>
                          <th className={classes.th}>ระดับ / งานที่ฝึก</th>
                          <th className={classes.th}>สาขาวิชา</th>
                          <th className={classes.th}>กลุ่มงานที่ฝึก</th>
                          <th className={classes.th}>ช่วงเวลาฝึกปฏิบัติ</th>
                          <th className={classes.thCenter}>ปีงบ</th>
                          <th className={classes.thCenter}>ห้องพัก</th>
                          <th className={classes.thCenter}>สถานะ</th>
                          <th className={classes.thCenter} style={{ width: "120px" }}>จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedList.length === 0 ? (
                          <tr>
                            <td colSpan="12" style={{ textAlign: "center", padding: "30px", color: "#888" }}>
                              ไม่พบข้อมูลนักศึกษาตามเงื่อนไขที่ค้นหา
                            </td>
                          </tr>
                        ) : (
                          paginatedList.map((std, idx) => (
                            <tr key={std.id} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fafcff" }}>
                              <td className={classes.td} style={{ textAlign: "center", fontWeight: "bold", color: "#666" }}>
                                {(currentPage - 1) * pageSize + idx + 1}
                              </td>
                              <td className={classes.td}>
                                <div style={{ fontWeight: "600", color: "#2e7d32", fontSize: "0.85rem" }}>
                                  {std.bookNo || "-"}
                                </div>
                                <div style={{ fontSize: "0.82rem", color: "#555" }}>
                                  {std.institution}
                                </div>
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                <Badge color="secondary" pill>
                                  {std.batchCount || 1} คน
                                </Badge>
                              </td>
                              <td className={classes.td}>
                                <div style={{ fontWeight: "bold", color: "#222" }}>
                                  {std.name}
                                </div>
                              </td>
                              <td className={classes.td}>
                                <div style={{ fontSize: "0.85rem" }}>{std.level}</div>
                                <Badge color="success" style={{ fontSize: "0.75rem", marginTop: "2px" }}>
                                  {std.role}
                                </Badge>
                              </td>
                              <td className={classes.td}>
                                <span style={{ fontSize: "0.83rem", color: "#555" }}>{std.major || "-"}</span>
                              </td>
                              <td className={classes.td}>
                                <span style={{ fontSize: "0.85rem", color: "#333" }}>{std.department}</span>
                              </td>
                              <td className={classes.td}>
                                <div style={{ fontSize: "0.85rem", fontWeight: "500" }}>{std.dateRange}</div>
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                <Badge color="dark" style={{ fontSize: "0.8rem", padding: "4px 7px" }}>
                                  {std.fiscalYear}
                                </Badge>
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                {renderRoomBadge(std.requiresAccommodation, std.roomNumber, std.dormPaymentStatus)}
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                {renderStatusBadge(std)}
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                <Button
                                  color="info"
                                  simple
                                  justIcon
                                  size="sm"
                                  title="ดูรายละเอียด"
                                  onClick={() => { setSelectedItem(std); setViewModal(true); }}
                                >
                                  <VisibilityIcon style={{ fontSize: "1.1rem" }} />
                                </Button>
                                <Button
                                  color="warning"
                                  simple
                                  justIcon
                                  size="sm"
                                  title="แก้ไข"
                                  onClick={() => handleOpenEditModal(std)}
                                >
                                  <EditIcon style={{ fontSize: "1.1rem" }} />
                                </Button>
                                <Button
                                  color="danger"
                                  simple
                                  justIcon
                                  size="sm"
                                  title="ลบ"
                                  onClick={() => handleDeleteItem(std.id, "STUDENT")}
                                >
                                  <DeleteIcon style={{ fontSize: "1.1rem" }} />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* TAB 2: ปฏิทินรายเดือน Matrix (ต.ค. - ก.ย.) */}
              {activeTab === 2 && (
                <div>
                  {/* แถบการ์ดสรุปยอดผู้ฝึกอบรมรายเดือน 12 เดือน (ต.ค. - ก.ย.) */}
                  <div style={{ marginBottom: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", flexWrap: "wrap", gap: "8px" }}>
                      <div>
                        <span style={{ fontWeight: "800", fontSize: "0.95rem", color: "#1e293b" }}>
                          📊 สรุปยอดผู้ฝึกอบรมแยกรายเดือน ปีงบประมาณ {selectedFiscalYear === "ALL" ? "ทั้งหมด" : selectedFiscalYear}
                        </span>
                        <span style={{ fontSize: "0.8rem", color: "#64748b", marginLeft: "10px" }}>
                          (คลิกที่เดือนเพื่อกรองดูเฉพาะผู้ที่ฝึกในเดือนนั้น)
                        </span>
                      </div>
                      {matrixMonthFilter !== "ALL" && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          style={{ fontSize: "0.78rem", fontWeight: "700", padding: "2px 10px", borderRadius: "15px" }}
                          onClick={() => setMatrixMonthFilter("ALL")}
                        >
                          ✕ ยกเลิกการกรองเดือน ({matrixMonthFilter}) แสดงทั้งปี
                        </button>
                      )}
                    </div>

                    {/* 12 Month Grid Cards */}
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(85px, 1fr))",
                      gap: "8px"
                    }}>
                      {MONTH_COLS.map((col) => {
                        const isCurrent = col.key === currentThaiMonth;
                        const isSelected = matrixMonthFilter === col.key;
                        const countObj = monthlyStats.stats[col.key] || { total: 0, doctors: 0, students: 0 };
                        const isPeak = col.key === monthlyStats.peakMonth && countObj.total > 0;

                        return (
                          <div
                            key={col.key}
                            onClick={() => setMatrixMonthFilter(prev => prev === col.key ? "ALL" : col.key)}
                            style={{
                              cursor: "pointer",
                              backgroundColor: isSelected
                                ? "#0284c7"
                                : isCurrent
                                ? "#f0fdf4"
                                : "#ffffff",
                              border: isSelected
                                ? "2px solid #0369a1"
                                : isCurrent
                                ? "2px solid #22c55e"
                                : "1px solid #e2e8f0",
                              borderRadius: "8px",
                              padding: "8px 6px",
                              textAlign: "center",
                              transition: "all 0.15s ease-in-out",
                              boxShadow: isSelected
                                ? "0 4px 6px -1px rgba(2, 132, 199, 0.3)"
                                : isCurrent
                                ? "0 2px 5px rgba(34, 197, 94, 0.2)"
                                : "0 1px 2px rgba(0,0,0,0.04)"
                            }}
                            title={`คลิกเพื่อกรองเฉพาะเดือน ${col.label}`}
                          >
                            {/* Month Name & Badge */}
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "3px", marginBottom: "4px" }}>
                              <span style={{
                                fontWeight: "800",
                                fontSize: "0.85rem",
                                color: isSelected ? "#ffffff" : isCurrent ? "#15803d" : "#334155"
                              }}>
                                {col.key}
                              </span>
                              {isCurrent && (
                                <span style={{
                                  backgroundColor: isSelected ? "#ffffff" : "#22c55e",
                                  color: isSelected ? "#0284c7" : "#ffffff",
                                  fontSize: "0.6rem",
                                  fontWeight: "800",
                                  padding: "1px 4px",
                                  borderRadius: "4px"
                                }}>
                                  นี้
                                </span>
                              )}
                              {isPeak && !isCurrent && (
                                <span style={{
                                  backgroundColor: isSelected ? "#ffffff" : "#fef3c7",
                                  color: isSelected ? "#d97706" : "#b45309",
                                  fontSize: "0.6rem",
                                  fontWeight: "800",
                                  padding: "1px 4px",
                                  borderRadius: "4px"
                                }}>
                                  🔥
                                </span>
                              )}
                            </div>

                            {/* Total Count */}
                            <div style={{
                              fontSize: "1.2rem",
                              fontWeight: "900",
                              lineHeight: "1.2",
                              color: isSelected ? "#ffffff" : countObj.total > 0 ? "#0f172a" : "#94a3b8"
                            }}>
                              {countObj.total}
                              <span style={{ fontSize: "0.7rem", fontWeight: "600", marginLeft: "2px" }}>คน</span>
                            </div>

                            {/* Breakdown */}
                            <div style={{
                              fontSize: "0.68rem",
                              marginTop: "4px",
                              color: isSelected ? "rgba(255,255,255,0.9)" : "#64748b",
                              fontWeight: "600"
                            }}>
                              👨‍⚕️{countObj.doctors} 🎓{countObj.students}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Header & Filter Controls */}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginBottom: "14px",
                    backgroundColor: "#f8fafc",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0"
                  }}>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                      <span style={{ fontSize: "0.88rem", fontWeight: "700", color: "#475569" }}>
                        แสดงกลุ่ม:
                      </span>
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          type="button"
                          className={`btn ${matrixTypeFilter === "ALL" ? "btn-primary" : "btn-outline-secondary"}`}
                          style={{ fontWeight: "700", fontSize: "0.8rem", padding: "5px 12px" }}
                          onClick={() => setMatrixTypeFilter("ALL")}
                        >
                          👥 ทั้งหมด ({filteredDoctors.length + filteredStudents.length})
                        </button>
                        <button
                          type="button"
                          className={`btn ${matrixTypeFilter === "DOCTOR" ? "btn-info text-white" : "btn-outline-info"}`}
                          style={{ fontWeight: "700", fontSize: "0.8rem", padding: "5px 12px" }}
                          onClick={() => setMatrixTypeFilter("DOCTOR")}
                        >
                          👨‍⚕️ แพทย์ฝึกอบรม ({filteredDoctors.length})
                        </button>
                        <button
                          type="button"
                          className={`btn ${matrixTypeFilter === "STUDENT" ? "btn-success" : "btn-outline-success"}`}
                          style={{ fontWeight: "700", fontSize: "0.8rem", padding: "5px 12px" }}
                          onClick={() => setMatrixTypeFilter("STUDENT")}
                        >
                          🎓 นักศึกษาฝึกงาน ({filteredStudents.length})
                        </button>
                      </div>

                      {matrixMonthFilter !== "ALL" && (
                        <span style={{
                          backgroundColor: "#e0f2fe",
                          color: "#0369a1",
                          border: "1px solid #7dd3fc",
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontSize: "0.8rem",
                          fontWeight: "700"
                        }}>
                          📅 กรองเฉพาะเดือน {matrixMonthFilter}: {matrixDataList.length} คน
                        </span>
                      )}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontWeight: "600", fontSize: "0.8rem" }}
                        onClick={() => window.print()}
                      >
                        🖨️ พิมพ์ตาราง
                      </button>
                      <span style={{ fontSize: "0.82rem", color: "#64748b" }}>
                        พบทั้งหมด <strong>{matrixDataList.length}</strong> รายการ
                      </span>
                    </div>
                  </div>

                  {/* Matrix Table */}
                  <div style={{ overflowX: "auto", border: "1px solid #cbd5e1", borderRadius: "8px" }}>
                    <table className={classes.table} style={{ margin: 0 }}>
                      <thead>
                        <tr style={{ backgroundColor: "#0f172a", color: "#ffffff" }}>
                          <th style={{ width: "45px", textAlign: "center", padding: "10px 4px", fontSize: "0.85rem", color: "#cbd5e1" }}>#</th>
                          <th style={{ minWidth: "220px", padding: "10px 10px", fontSize: "0.85rem", color: "#ffffff" }}>ชื่อ-สกุล / ต้นสังกัด</th>
                          <th style={{ width: "130px", padding: "10px 8px", fontSize: "0.85rem", color: "#ffffff" }}>ประเภท / ระดับ</th>
                          <th style={{ minWidth: "160px", padding: "10px 8px", fontSize: "0.85rem", color: "#ffffff" }}>กลุ่มงานที่ฝึก</th>
                          <th style={{ minWidth: "140px", padding: "10px 8px", fontSize: "0.85rem", color: "#ffffff" }}>ช่วงเวลาฝึก</th>
                          <th style={{ width: "90px", textAlign: "center", padding: "10px 4px", fontSize: "0.85rem", color: "#ffffff" }}>ห้องพัก</th>
                          {MONTH_COLS.map((col) => {
                            const isCurrent = col.key === currentThaiMonth;
                            const isColSelected = matrixMonthFilter === col.key;
                            return (
                              <th
                                key={col.key}
                                style={{
                                  width: "60px",
                                  textAlign: "center",
                                  padding: "8px 2px",
                                  fontSize: "0.82rem",
                                  fontWeight: "800",
                                  backgroundColor: isColSelected
                                    ? "#0284c7"
                                    : isCurrent
                                    ? "#16a34a"
                                    : "#1e293b",
                                  color: "#ffffff",
                                  borderLeft: "1px solid rgba(255,255,255,0.15)",
                                  cursor: "pointer"
                                }}
                                onClick={() => setMatrixMonthFilter(prev => prev === col.key ? "ALL" : col.key)}
                                title={`คลิกเพื่อกรองเฉพาะเดือน ${col.label}`}
                              >
                                <div>{col.key}</div>
                                {isCurrent && (
                                  <div style={{ fontSize: "0.62rem", fontWeight: "600", color: "#bbf7d0" }}>
                                    (เดือนนี้)
                                  </div>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {matrixDataList.length === 0 ? (
                          <tr>
                            <td colSpan="18" style={{ textAlign: "center", padding: "35px", color: "#64748b" }}>
                              ไม่พบข้อมูลผู้ฝึกตามเงื่อนไขที่เลือก
                            </td>
                          </tr>
                        ) : (
                          matrixDataList.map((item, idx) => {
                            const attending = item.months || getAttendingMonths(item);
                            return (
                              <tr
                                key={item.id || idx}
                                style={{
                                  backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                                  borderBottom: "1px solid #e2e8f0"
                                }}
                              >
                                <td style={{ textAlign: "center", fontWeight: "700", color: "#64748b", fontSize: "0.8rem", padding: "8px 4px" }}>
                                  {idx + 1}
                                </td>
                                <td style={{ padding: "8px 10px" }}>
                                  <div style={{ fontWeight: "700", color: "#1e293b", fontSize: "0.88rem" }}>
                                    {item.nameTH || item.name}
                                  </div>
                                  <div style={{ fontSize: "0.76rem", color: "#64748b" }}>
                                    {item.institution}
                                  </div>
                                </td>
                                <td style={{ padding: "8px 8px" }}>
                                  {item.type === "DOCTOR" ? (
                                    <span style={{
                                      backgroundColor: "#e0f2fe",
                                      color: "#0369a1",
                                      border: "1px solid #bae6fd",
                                      borderRadius: "5px",
                                      padding: "2px 7px",
                                      fontSize: "0.75rem",
                                      fontWeight: "700",
                                      display: "inline-block"
                                    }}>
                                      👨‍⚕️ {item.doctorType || "แพทย์"}
                                    </span>
                                  ) : (
                                    <span style={{
                                      backgroundColor: "#dcfce7",
                                      color: "#15803d",
                                      border: "1px solid #bbf7d0",
                                      borderRadius: "5px",
                                      padding: "2px 7px",
                                      fontSize: "0.75rem",
                                      fontWeight: "700",
                                      display: "inline-block"
                                    }}>
                                      🎓 {item.role || "นศ.ฝึกงาน"} {item.batchCount > 1 && `(${item.batchCount} คน)`}
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: "8px 8px", fontSize: "0.82rem", color: "#334155" }}>
                                  {item.department}
                                </td>
                                <td style={{ padding: "8px 8px", fontSize: "0.78rem", color: "#475569" }}>
                                  {item.dateRange || item.dateRangeText || "-"}
                                </td>
                                <td style={{ textAlign: "center", padding: "8px 4px" }}>
                                  {item.requiresAccommodation && item.roomNumber && item.roomNumber !== "ไม่ได้พัก" ? (
                                    <span style={{
                                      backgroundColor: "#f1f5f9",
                                      color: "#0f172a",
                                      border: "1px solid #cbd5e1",
                                      borderRadius: "4px",
                                      padding: "2px 5px",
                                      fontSize: "0.75rem",
                                      fontWeight: "700"
                                    }}>
                                      🏨 {item.roomNumber}
                                    </span>
                                  ) : (
                                    <span style={{ color: "#94a3b8", fontSize: "0.74rem" }}>ไม่ได้พัก</span>
                                  )}
                                </td>

                                {/* 12 Month Cells */}
                                {MONTH_COLS.map((col) => {
                                  const isAttending = attending.includes(col.key);
                                  const isCurrent = col.key === currentThaiMonth;
                                  const isColSelected = matrixMonthFilter === col.key;

                                  return (
                                    <td
                                      key={col.key}
                                      style={{
                                        textAlign: "center",
                                        padding: "6px 2px",
                                        borderLeft: "1px solid #e2e8f0",
                                        backgroundColor: isColSelected
                                          ? "rgba(2, 132, 199, 0.08)"
                                          : isCurrent
                                          ? "rgba(34, 197, 94, 0.06)"
                                          : "transparent"
                                      }}
                                    >
                                      {isAttending ? (
                                        item.type === "DOCTOR" ? (
                                          <span style={{
                                            backgroundColor: "#0284c7",
                                            color: "#ffffff",
                                            borderRadius: "4px",
                                            padding: "3px 6px",
                                            fontSize: "0.73rem",
                                            fontWeight: "800",
                                            display: "inline-block"
                                          }}>
                                            ✓ ฝึก
                                          </span>
                                        ) : (
                                          <span style={{
                                            backgroundColor: "#16a34a",
                                            color: "#ffffff",
                                            borderRadius: "4px",
                                            padding: "3px 6px",
                                            fontSize: "0.73rem",
                                            fontWeight: "800",
                                            display: "inline-block"
                                          }}>
                                            ✓ ฝึก
                                          </span>
                                        )
                                      ) : (
                                        <span style={{ color: "#cbd5e1", fontSize: "0.8rem" }}>-</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                      <tfoot>
                        {/* Footer Row 1: Doctor Breakdown */}
                        {(matrixTypeFilter === "ALL" || matrixTypeFilter === "DOCTOR") && (
                          <tr style={{ backgroundColor: "#f0f9ff", borderTop: "2px solid #bae6fd" }}>
                            <td colSpan="6" style={{ padding: "8px 12px", textAlign: "right", fontWeight: "800", color: "#0369a1", fontSize: "0.85rem" }}>
                              👨‍⚕️ ยอดรวมเฉพาะแพทย์ (คน):
                            </td>
                            {MONTH_COLS.map((col) => {
                              const docCount = (matrixTypeFilter === "STUDENT" ? [] : filteredDoctors).filter(d => {
                                const m = d.months || getAttendingMonths(d);
                                return m.includes(col.key);
                              }).length;
                              const isCurrent = col.key === currentThaiMonth;
                              return (
                                <td
                                  key={col.key}
                                  style={{
                                    textAlign: "center",
                                    padding: "8px 2px",
                                    fontWeight: "800",
                                    color: "#0284c7",
                                    fontSize: "0.88rem",
                                    borderLeft: "1px solid #bae6fd",
                                    backgroundColor: isCurrent ? "#e0f2fe" : "transparent"
                                  }}
                                >
                                  {docCount}
                                </td>
                              );
                            })}
                          </tr>
                        )}

                        {/* Footer Row 2: Student Breakdown */}
                        {(matrixTypeFilter === "ALL" || matrixTypeFilter === "STUDENT") && (
                          <tr style={{ backgroundColor: "#f0fdf4", borderTop: "1px solid #bbf7d0" }}>
                            <td colSpan="6" style={{ padding: "8px 12px", textAlign: "right", fontWeight: "800", color: "#15803d", fontSize: "0.85rem" }}>
                              🎓 ยอดรวมเฉพาะนักศึกษา (คน):
                            </td>
                            {MONTH_COLS.map((col) => {
                              const stuCount = (matrixTypeFilter === "DOCTOR" ? [] : filteredStudents).filter(s => {
                                const m = s.months || getAttendingMonths(s);
                                return m.includes(col.key);
                              }).length;
                              const isCurrent = col.key === currentThaiMonth;
                              return (
                                <td
                                  key={col.key}
                                  style={{
                                    textAlign: "center",
                                    padding: "8px 2px",
                                    fontWeight: "800",
                                    color: "#16a34a",
                                    fontSize: "0.88rem",
                                    borderLeft: "1px solid #bbf7d0",
                                    backgroundColor: isCurrent ? "#dcfce7" : "transparent"
                                  }}
                                >
                                  {stuCount}
                                </td>
                              );
                            })}
                          </tr>
                        )}

                        {/* Footer Row 3: Grand Total */}
                        <tr style={{ backgroundColor: "#0f172a", color: "#ffffff", borderTop: "2px solid #334155" }}>
                          <td colSpan="6" style={{ padding: "10px 12px", textAlign: "right", fontWeight: "900", fontSize: "0.92rem", color: "#ffffff" }}>
                            🌟 ยอดรวมผู้ฝึกทั้งหมด (คน):
                          </td>
                          {MONTH_COLS.map((col) => {
                            const isCurrent = col.key === currentThaiMonth;
                            const targetDocs = (matrixTypeFilter === "STUDENT") ? [] : filteredDoctors;
                            const targetStus = (matrixTypeFilter === "DOCTOR") ? [] : filteredStudents;
                            const totalInMonth = [...targetDocs, ...targetStus].filter(item => {
                              const m = item.months || getAttendingMonths(item);
                              return m.includes(col.key);
                            }).length;

                            return (
                              <td
                                key={col.key}
                                style={{
                                  textAlign: "center",
                                  padding: "10px 2px",
                                  fontWeight: "900",
                                  fontSize: "1rem",
                                  color: isCurrent ? "#4ade80" : "#fde047",
                                  borderLeft: "1px solid rgba(255,255,255,0.2)",
                                  backgroundColor: isCurrent ? "rgba(34, 197, 94, 0.2)" : "transparent"
                                }}
                              >
                                {totalInMonth}
                              </td>
                            );
                          })}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Pagination (for Tab 0 and Tab 1) */}
              {activeTab !== 2 && (
                <div style={{ marginTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ color: "#666", fontSize: "0.88rem" }}>
                    แสดง {activeList.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} ถึง{" "}
                    {Math.min(currentPage * pageSize, activeList.length)} จากทั้งหมด {activeList.length} รายการ
                  </div>
                  <div>
                    <Pagination
                      current={currentPage}
                      total={totalPages}
                      onPageChange={(page) => setCurrentPage(page)}
                    />
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Modal: View Details */}
      <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg" style={{ fontFamily: '"Sarabun", sans-serif' }}>
        <ModalHeader toggle={() => setViewModal(false)} style={{ backgroundColor: selectedItem?.type === "DOCTOR" ? "#00838f" : "#2e7d32", color: "#fff" }}>
          {selectedItem?.type === "DOCTOR" ? "👨‍⚕️ รายละเอียดข้อมูลแพทย์ฝึกอบรม" : "🎓 รายละเอียดข้อมูลนักศึกษาฝึกงาน"}
        </ModalHeader>
        <ModalBody>
          {selectedItem && (
            <div style={{ padding: "10px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: "12px", marginBottom: "15px" }}>
                <div>
                  <h4 style={{ margin: 0, fontWeight: "bold", color: "#333" }}>
                    {selectedItem.nameTH || selectedItem.name}
                  </h4>
                  {selectedItem.nameEN && (
                    <div style={{ color: "#666", fontStyle: "italic", fontSize: "0.95rem" }}>
                      {selectedItem.nameEN}
                    </div>
                  )}
                </div>
                <div>
                  {renderStatusBadge(selectedItem.status)}
                </div>
              </div>

              <div className="row" style={{ fontSize: "0.95rem", lineHeight: "1.9" }}>
                <div className="col-md-6">
                  <div><strong>ประเภท:</strong> {selectedItem.type === "DOCTOR" ? "แพทย์ฝึกอบรม / ศึกษาดูงาน" : "นักศึกษาฝึกงาน"}</div>
                  <div><strong>เลขที่หนังสือส่งตัว:</strong> {selectedItem.bookNo || "-"}</div>
                  <div><strong>สถาบัน / ต้นสังกัด:</strong> {selectedItem.institution}</div>
                  {selectedItem.type === "DOCTOR" ? (
                    <>
                      <div><strong>ตำแหน่งแพทย์:</strong> {selectedItem.doctorType}</div>
                      <div><strong>ตำแหน่งทางวิชาการ:</strong> {selectedItem.position}</div>
                      <div><strong>เลขที่ใบประกอบวิชาชีพ (ว.):</strong> {selectedItem.medicalLicenseNo || "-"}</div>
                      <div><strong>เลขประจำตัวประชาชน:</strong> {selectedItem.citizenId || "-"}</div>
                      <div><strong>วัน/เดือน/ปีเกิด:</strong> {selectedItem.birthDate && moment(selectedItem.birthDate).isValid() ? moment(selectedItem.birthDate).format("D MMMM BBBB") : (selectedItem.birthDate || "-")}</div>
                    </>
                  ) : (
                    <>
                      <div><strong>ระดับ / ชั้นปี:</strong> {selectedItem.level}</div>
                      <div><strong>งานที่ฝึก:</strong> {selectedItem.role}</div>
                      <div><strong>สาขาวิชา:</strong> {selectedItem.major || "-"}</div>
                      <div><strong>จำนวนคนในหนังสือ:</strong> {selectedItem.batchCount} คน</div>
                    </>
                  )}
                </div>
                <div className="col-md-6">
                  <div><strong>กลุ่มงานที่ฝึกใน รพ.:</strong> {selectedItem.department}</div>
                  <div><strong>ช่วงเวลาฝึกปฏิบัติ:</strong> {selectedItem.dateRange}</div>
                  <div><strong>วันที่เริ่มฝึกวันแรก:</strong> {selectedItem.startDate && moment(selectedItem.startDate).isValid() ? moment(selectedItem.startDate).format("D MMMM BBBB") : (selectedItem.startDate || "-")}</div>
                  <div><strong>วันที่สิ้นสุด:</strong> {selectedItem.endDate && moment(selectedItem.endDate).isValid() ? moment(selectedItem.endDate).format("D MMMM BBBB") : (selectedItem.endDate || "-")}</div>
                  <div><strong>ปีงบประมาณไทย:</strong> <Badge color="dark">{selectedItem.fiscalYear}</Badge> (1 ต.ค. - 30 ก.ย.)</div>
                  <hr style={{ margin: "10px 0" }} />
                  <div>
                    <strong>การขอห้องพัก:</strong>{" "}
                    {selectedItem.requiresAccommodation ? (
                      <Badge color="info" style={{ fontSize: "0.88rem" }}>
                        พักห้อง {selectedItem.roomNumber} ({selectedItem.dormFee || "500 บาท/เดือน"})
                      </Badge>
                    ) : (
                      <Badge color="light" style={{ color: "#666", border: "1px solid #ccc" }}>ไม่ได้พักหอพัก</Badge>
                    )}
                  </div>
                  <div><strong>เบอร์โทรติดต่อ:</strong> {selectedItem.phone || "-"}</div>
                  <div><strong>หมายเหตุ:</strong> {selectedItem.notes || "-"}</div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setViewModal(false)}>ปิด</Button>
        </ModalFooter>
      </Modal>

      {/* Modal: Add / Edit Form */}
      <Modal isOpen={formModal} toggle={() => setFormModal(false)} size="lg" style={{ fontFamily: '"Sarabun", sans-serif' }}>
        <ModalHeader toggle={() => setFormModal(false)} style={{ backgroundColor: formType === "DOCTOR" ? "#00838f" : "#2e7d32", color: "#fff" }}>
          {isEditing ? `✏️ แก้ไขข้อมูล${formType === "DOCTOR" ? "แพทย์" : "นักศึกษา"}` : `➕ เพิ่มข้อมูล${formType === "DOCTOR" ? "แพทย์ใหม่" : "นักศึกษาใหม่"}`}
        </ModalHeader>
        <ModalBody>
          <div style={{ padding: "5px" }}>
            {/* Toggle Doctor vs Student (if Add mode) */}
            {!isEditing && (
              <div style={{ marginBottom: "15px", padding: "10px", backgroundColor: "#f0f4f8", borderRadius: "5px", display: "flex", alignItems: "center", gap: "20px" }}>
                <span style={{ fontWeight: "bold" }}>เลือกประเภทผู้ฝึกอบรม:</span>
                <label style={{ margin: 0, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="formType"
                    checked={formType === "DOCTOR"}
                    onChange={() => setFormType("DOCTOR")}
                    style={{ marginRight: "6px" }}
                  />
                  👨‍⚕️ แพทย์ฝึกอบรม / ดูงาน
                </label>
                <label style={{ margin: 0, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="formType"
                    checked={formType === "STUDENT"}
                    onChange={() => setFormType("STUDENT")}
                    style={{ marginRight: "6px" }}
                  />
                  🎓 นักศึกษาฝึกงาน
                </label>
              </div>
            )}

            <div className="row g-3">
              {/* Common Fields */}
              <div className="col-md-6 mb-2">
                <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>เลขที่หนังสือส่งตัว</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="เช่น อว 68104.19/6/-00745, บร 0033.105/6551"
                  value={formData.bookNo}
                  onChange={(e) => setFormData({ ...formData, bookNo: e.target.value })}
                />
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>สถาบันการศึกษา / หน่วยงานต้นสังกัด *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="เช่น มหาวิทยาลัยสงขลานครินทร์, โรงพยาบาลบุรีรัมย์"
                  value={formData.institution}
                  onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                  required
                />
              </div>

              {/* Name Fields */}
              <div className="col-md-6 mb-2">
                <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>ชื่อ-สกุล (ภาษาไทย) *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="เช่น พญ.เกศราภรณ์ เสาะหายิ่ง หรือ นางสาวคอลีเย๊าะ ตูโรง"
                  value={formData.nameTH}
                  onChange={(e) => setFormData({ ...formData, nameTH: e.target.value })}
                  required
                />
              </div>

              {formType === "DOCTOR" ? (
                <>
                  <div className="col-md-6 mb-2">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>ชื่อ-สกุล (English)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="เช่น Miss Oranat Tanprasertkul"
                      value={formData.nameEN}
                      onChange={(e) => setFormData({ ...formData, nameEN: e.target.value })}
                    />
                  </div>

                  <div className="col-md-4 mb-2">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>ตำแหน่งแพทย์</label>
                    <select
                      className="form-control"
                      value={formData.doctorType}
                      onChange={(e) => setFormData({ ...formData, doctorType: e.target.value })}
                    >
                      <option value="แพทย์ elective">แพทย์ elective</option>
                      <option value="แพทย์ fellow">แพทย์ fellow</option>
                      <option value="แพทย์ประจำบ้าน">แพทย์ประจำบ้าน</option>
                      <option value="นักศึกษาแพทย์">นักศึกษาแพทย์</option>
                    </select>
                  </div>

                  <div className="col-md-4 mb-2">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>ตำแหน่ง / ชั้นปี</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="เช่น แพทย์ปฏิบัติการชั้นปีที่ 1, นักศึกษาแพทย์ ปี 6"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    />
                  </div>

                  <div className="col-md-4 mb-2">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>เลขใบประกอบวิชาชีพ (เลข ว.)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="เช่น 65549, 72149"
                      value={formData.medicalLicenseNo}
                      onChange={(e) => setFormData({ ...formData, medicalLicenseNo: e.target.value })}
                    />
                  </div>

                  <div className="col-md-6 mb-2">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>เลขประจำตัวประชาชน (13 หลัก)</label>
                    <input
                      type="text"
                      className="form-control"
                      maxLength="13"
                      placeholder="เช่น 1100501255181"
                      value={formData.citizenId}
                      onChange={(e) => setFormData({ ...formData, citizenId: e.target.value })}
                    />
                  </div>

                  <div className="col-md-6 mb-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                        วัน/เดือน/ปีเกิด (พ.ศ.)
                      </label>
                      {formData.birthDate && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0"
                          style={{ fontSize: "0.74rem", textDecoration: "underline" }}
                          onClick={() => setFormData({ ...formData, birthDate: "" })}
                        >
                          ล้างวันที่
                        </button>
                      )}
                    </div>
                    <Datetime
                      value={formData.birthDate && moment(formData.birthDate).isValid() ? moment(formData.birthDate) : null}
                      renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                      inputProps={{
                        placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                        readOnly: "readonly",
                        style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold" },
                        className: "form-control",
                        value: formData.birthDate && moment(formData.birthDate).isValid() ? moment(formData.birthDate).format("D MMMM BBBB") : ""
                      }}
                      closeOnSelect={true}
                      closeOnTab={true}
                      dateFormat="D MMMM BBBB"
                      timeFormat={false}
                      onChange={(val) => {
                        if (moment.isMoment(val) && val.isValid()) {
                          setFormData({ ...formData, birthDate: val.format("YYYY-MM-DD") });
                        } else if (val && typeof val === "string") {
                          const m = moment(val);
                          if (m.isValid()) setFormData({ ...formData, birthDate: m.format("YYYY-MM-DD") });
                        } else if (!val) {
                          setFormData({ ...formData, birthDate: "" });
                        }
                      }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="col-md-6 mb-2">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>ระดับ / ชั้นปี</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="เช่น ปี 4, ปี 6 ผลัด 6, ม.5, ปวส.1"
                      value={formData.level}
                      onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    />
                  </div>

                  <div className="col-md-4 mb-2">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>งานที่ฝึก</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="เช่น นศ.ห้องเภสัชกรรม, นศ.ห้องแลปฯ, ผู้ช่วยการพยาบาล"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    />
                  </div>

                  <div className="col-md-4 mb-2">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>สาขาวิชา</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="เช่น เภสัชกรรม, คอมพิวเตอร์ธุรกิจ, จุลชีววิทยา"
                      value={formData.major}
                      onChange={(e) => setFormData({ ...formData, major: e.target.value })}
                    />
                  </div>

                  <div className="col-md-4 mb-2">
                    <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>จำนวนคนตามหนังสือ</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      value={formData.batchCount}
                      onChange={(e) => setFormData({ ...formData, batchCount: Number(e.target.value) })}
                    />
                  </div>
                </>
              )}

              {/* Department & Dates (เชื่อมโยงฐานข้อมูล รพ.) */}
              <div className="col-md-6 mb-2">
                <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>กลุ่มงานที่ฝึกใน รพ. *</label>
                <select
                  className="form-control"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                >
                  {departmentsList.map((deptName) => (
                    <option key={deptName} value={deptName}>
                      {deptName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>สถานะการฝึกอบรม</label>
                <select
                  className="form-control"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="กำลังฝึกอบรม">กำลังฝึกอบรม / กำลังฝึกปฏิบัติงาน</option>
                  <option value="รอเริ่มฝึก">รอเริ่มฝึก</option>
                  <option value="สำเร็จการฝึกอบรม">สำเร็จการฝึกอบรม</option>
                  <option value="ยกเลิก/ไม่มา">ยกเลิก/ไม่มา</option>
                </select>
              </div>

              <div className="col-md-4 mb-2">
                <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                  วันที่เริ่มฝึกวันแรก * (พ.ศ.)
                </label>
                <Datetime
                  value={formData.startDate && moment(formData.startDate).isValid() ? moment(formData.startDate) : null}
                  renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                  inputProps={{
                    placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                    readOnly: "readonly",
                    style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold" },
                    className: "form-control",
                    value: formData.startDate && moment(formData.startDate).isValid() ? moment(formData.startDate).format("D MMMM BBBB") : ""
                  }}
                  closeOnSelect={true}
                  closeOnTab={true}
                  dateFormat="D MMMM BBBB"
                  timeFormat={false}
                  onChange={(val) => {
                    if (moment.isMoment(val) && val.isValid()) {
                      handleStartDateChange(val.format("YYYY-MM-DD"));
                    } else if (val && typeof val === "string") {
                      const m = moment(val);
                      if (m.isValid()) handleStartDateChange(m.format("YYYY-MM-DD"));
                    } else if (!val) {
                      handleStartDateChange("");
                    }
                  }}
                />
              </div>

              <div className="col-md-4 mb-2">
                <div className="d-flex justify-content-between align-items-center">
                  <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                    วันที่สิ้นสุดการฝึก (พ.ศ.)
                  </label>
                  {formData.endDate && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0"
                      style={{ fontSize: "0.74rem", textDecoration: "underline" }}
                      onClick={() => handleEndDateChange("")}
                    >
                      ล้างวันที่
                    </button>
                  )}
                </div>
                <Datetime
                  value={formData.endDate && moment(formData.endDate).isValid() ? moment(formData.endDate) : null}
                  renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                  inputProps={{
                    placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                    readOnly: "readonly",
                    style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold" },
                    className: "form-control",
                    value: formData.endDate && moment(formData.endDate).isValid() ? moment(formData.endDate).format("D MMMM BBBB") : ""
                  }}
                  closeOnSelect={true}
                  closeOnTab={true}
                  dateFormat="D MMMM BBBB"
                  timeFormat={false}
                  onChange={(val) => {
                    if (moment.isMoment(val) && val.isValid()) {
                      handleEndDateChange(val.format("YYYY-MM-DD"));
                    } else if (val && typeof val === "string") {
                      const m = moment(val);
                      if (m.isValid()) handleEndDateChange(m.format("YYYY-MM-DD"));
                    } else if (!val) {
                      handleEndDateChange("");
                    }
                  }}
                />
              </div>

              <div className="col-md-4 mb-2">
                <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem", color: "#6a1b9a" }}>
                  ปีงบประมาณ (คำนวณอัตโนมัติ)
                </label>
                <input
                  type="number"
                  className="form-control"
                  style={{ backgroundColor: "#f3e5f5", fontWeight: "bold" }}
                  value={formData.fiscalYear}
                  onChange={(e) => setFormData({ ...formData, fiscalYear: Number(e.target.value) })}
                />
                <small style={{ color: "#777", fontSize: "0.75rem", display: "block", marginTop: "3px" }}>
                  {formData.startDate
                    ? `ช่วง: ${formatThaiDateRangeText(formData.startDate, formData.endDate)}`
                    : "1 ต.ค. - 30 ก.ย. ตามระบบราชการไทย"}
                </small>
              </div>

              {/* ซ่อนช่องข้อความช่วงวันที่ (ตามหนังสือ) ตามที่ผู้ใช้ร้องขอ โดยระบบจะแปลงจากวันที่เริ่มและสิ้นสุดให้อัตโนมัติ */}
              {/*
              <div className="col-md-12 mb-2">
                <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>ข้อความช่วงวันที่ (ตามหนังสือ)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="เช่น 3 พ.ย.-16 พ.ย. 2568 หรือ 1 มิ.ย. - 8 ก.ย. 2569"
                  value={formData.dateRange}
                  onChange={(e) => setFormData({ ...formData, dateRange: e.target.value })}
                />
              </div>
              */}

              {/* Section: หอพัก / ห้องพัก */}
              <div className="col-md-12 mb-2" style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", padding: "14px", borderRadius: "10px" }}>
                <h6 style={{ margin: "0 0 10px 0", fontWeight: "bold", color: "#166534", fontSize: "0.98rem" }}>
                  🏠 ข้อมูลการขอห้องพัก / หอพักโรงพยาบาล
                </h6>
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <div
                      onClick={() => setFormData((prev) => ({ ...prev, requiresAccommodation: !prev.requiresAccommodation }))}
                      style={{
                        backgroundColor: formData.requiresAccommodation ? "#dcfce7" : "#ffffff",
                        border: formData.requiresAccommodation ? "2px solid #16a34a" : "2px dashed #94a3b8",
                        borderRadius: "10px",
                        padding: "14px 18px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "all 0.2s ease",
                        boxShadow: formData.requiresAccommodation ? "0 4px 10px rgba(22,163,74,0.18)" : "none"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                        <input
                          type="checkbox"
                          checked={Boolean(formData.requiresAccommodation)}
                          readOnly
                          style={{
                            width: "24px",
                            height: "24px",
                            cursor: "pointer",
                            accentColor: "#16a34a"
                          }}
                        />
                        <div>
                          <div style={{ fontSize: "1.08rem", fontWeight: "bold", color: formData.requiresAccommodation ? "#14532d" : "#1e293b" }}>
                            🏢 ขอเข้าพักหอพัก รพ. (มี 4 ตึก ตึกละ 3 ชั้น)
                          </div>
                          <div style={{ fontSize: "0.84rem", color: formData.requiresAccommodation ? "#15803d" : "#64748b", marginTop: "2px" }}>
                            {formData.requiresAccommodation
                              ? "✅ มีความประสงค์ขอเข้าพัก — กรุณาระบุตึก ชั้น และเลขห้องด้านล่าง"
                              : "⚪ ไม่ได้พักหอพัก รพ. (คลิกที่นี่เพื่อเปิดตัวเลือกจัดสรรห้องพัก)"}
                          </div>
                        </div>
                      </div>
                      <span
                        style={{
                          backgroundColor: formData.requiresAccommodation ? "#16a34a" : "#94a3b8",
                          color: "#ffffff",
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          padding: "6px 14px",
                          borderRadius: "20px",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {formData.requiresAccommodation ? "🟢 ขอพักหอพัก" : "⚪ ไม่ได้พัก"}
                      </span>
                    </div>
                  </div>
                  {formData.requiresAccommodation && (
                    <>
                      <div className="col-md-3">
                        <label className="form-label" style={{ fontSize: "0.85rem", margin: 0 }}>เลือกตึกหอพัก</label>
                        <select
                          className="form-control"
                          value={formData.building || "ตึก 1"}
                          onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                        >
                          <option value="ตึก 1">🏢 อาคาร 1 (ตึก 1)</option>
                          <option value="ตึก 2">🏢 อาคาร 2 (ตึก 2)</option>
                          <option value="ตึก 3">🏢 อาคาร 3 (ตึก 3)</option>
                          <option value="ตึก 4">🏢 อาคาร 4 (ตึก 4)</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label" style={{ fontSize: "0.85rem", margin: 0 }}>เลือกชั้น</label>
                        <select
                          className="form-control"
                          value={formData.floor || 1}
                          onChange={(e) => setFormData({ ...formData, floor: Number(e.target.value) })}
                        >
                          <option value={1}>ชั้น 1</option>
                          <option value={2}>ชั้น 2</option>
                          <option value={3}>ชั้น 3</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label" style={{ fontSize: "0.85rem", margin: 0 }}>ระบุเลขห้องพัก</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="เช่น 111, 231, 302"
                          value={formData.roomNumber === "ไม่ได้พัก" ? "" : formData.roomNumber}
                          onChange={(e) => setFormData({ ...formData, roomNumber: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label" style={{ fontSize: "0.85rem", margin: 0 }}>อัตราค่าหอพัก</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="เช่น 500 บาท/เดือน"
                          value={formData.dormFee}
                          onChange={(e) => setFormData({ ...formData, dormFee: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>เบอร์โทรศัพท์ติดต่อ</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="เช่น 081-234-5678"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div className="col-md-6 mb-2">
                <label className="form-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>หมายเหตุเพิ่มเติม</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="เช่น ย้ายห้อง, เลื่อนวันเริ่มฝึก"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setFormModal(false)}>ยกเลิก</Button>
          <Button color="success" onClick={handleSaveForm}>บันทึกข้อมูล</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
